import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Grid, Paper, Typography } from '@mui/material'

import Header from 'packages/eid-ui/Header' // Ensure this import is correct
import { Loader, Pagination } from 'packages/eid-ui'
import { useQuery, useDebounce } from 'packages/core'
import {
    useAllSSOApplications,
    useApplicationSettings,
    useAllCustomCategories,
    useRemoveApplicationFromCategory,
} from 'hooks'
import { useIsSmallScreen } from 'packages/core'
import SSOApplicationsCard from 'components/SSOApplicationsCard'
import ManageApplicationButton from './ManageApplicationsButton'
import ManageCategoryButton from './ManageCategoryButton'
import { useLocation } from 'react-router-dom'

const DEFAULT_TAKE = 15

const SSOApplications = ({ isCustomTab }) => {
    const { t } = useTranslation()

    const isSmallScreen = useIsSmallScreen()

    const { data: appConfig } = useApplicationSettings()
    const styleConfig = appConfig.style

    const [sorting, setSorting] = useState({ sortBy: '', sortOrder: '' })
    const [searchKey, setSearchKey] = useState('')
    const [page, setPage] = useState(1)
    const [perPageItems, setPerPageItems] = useState(DEFAULT_TAKE)
    const { data: customCategories, isLoading: isLoadingCategories } =
        useAllCustomCategories()
    const location = useLocation()
    const category = customCategories?.find(
        (c) => c.id == location.pathname?.split('/')?.pop(),
    )

    const {
        mutateAsync: removeApplicationFromCategory,
        isLoading: isRemovingApplicationFromCategory,
    } = useRemoveApplicationFromCategory()

    const showRecentlyUsed =
        location.pathname?.split('/')?.pop() == 'recentlyUsed'

    const handlePageChange = (_, value) => {
        setPage(value)
    }

    const handlePageSelection = (value) => {
        setPage(value)
    }
    const handleItemsPerPageChange = (value) => {
        setPage(1)
        setPerPageItems(value)
    }
    const skip = (page - 1) * perPageItems

    const debouncedValue = useDebounce(searchKey)

    const handleSearch = (val) => {
        setSearchKey(val)
        setPage(1)
    }

    const { data, isFetching, refetch } = useAllSSOApplications({
        skip,
        take: perPageItems,
        searchTerm: debouncedValue,
        sorting,
        recentlyUsed: showRecentlyUsed,
        categoryId: category?.id,
        showAssignedOnly: true,
    })

    const numberOfPages = data ? Math.ceil(data.totalCount / perPageItems) : 0

    const handleRemoveAppFromCategory = (appId) => {
        removeApplicationFromCategory({
            categoryId: category?.id,
            protectedApplicationsResourceIds: [appId],
        }).then(() => {
            refetch()
        })
    }

    if (isLoadingCategories) {
        return <Loader />
    }
    return (
        <Paper
            className={'root workflow'}
            data-ai-explain-section="listing"
        >
            <Box
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                {/* Below empty div is intentional instead of empty element <></> */}
                {!isCustomTab ? (
                    <div></div>
                ) : (
                    <Box
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            width: '100%',
                            gap: '2.4rem',
                            justifyContent: 'flex-start',
                        }}
                    >
                        <ManageApplicationButton
                            label={
                                <Typography sx={{ fontSize: '1.5rem' }}>
                                    {t('ManageApplications')}
                                </Typography>
                            }
                            category={category}
                        />

                        <ManageCategoryButton
                            label={
                                <Typography sx={{ fontSize: '1.5rem' }}>
                                    {t('ManageCategory')}
                                </Typography>
                            }
                            category={category}
                        />
                    </Box>
                )}

                <Box style={{ display: 'flex' }}>
                    {styleConfig.search?.show && (
                        <div
                            style={{
                                display: 'flex',
                                flex: 1,
                                alignItems: 'center',
                                padding: '0.8rem 0 1.6rem 0',
                                width: isSmallScreen ? '100%' : '32.5rem',
                                float: 'right',
                            }}
                            data-ai-explain="Search SSO applications"
                        >
                            <Header.Search
                                styleData={styleConfig.search?.style}
                                placeholder={t('Search')}
                                handleSearch={handleSearch}
                                showClearIcon={false}
                            />
                        </div>
                    )}
                </Box>
            </Box>
            <Grid
                container
                spacing={2}
                sx={{ justifyContent: 'start !important' }}
            >
                {isFetching || isRemovingApplicationFromCategory ? (
                    <Loader margin="auto" />
                ) : data?.data && data?.data.length > 0 ? (
                    data?.data.map((app) => {
                        return (
                            <Grid item key={app.id}>
                                <SSOApplicationsCard
                                    applicationName={app.friendlyName}
                                    iconUrl={app.applicationIcon}
                                    link={app.matchingPath}
                                    handleRemoveAppFromCategory={
                                        isCustomTab
                                            ? () =>
                                                  handleRemoveAppFromCategory(
                                                      app.id,
                                                  )
                                            : undefined
                                    }
                                />
                            </Grid>
                        )
                    })
                ) : (
                    <Grid
                        item
                        xs={12}
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                        data-ai-explain="No applications match your search"
                    >
                        {t('NoDataFound')}
                    </Grid>
                )}
            </Grid>
            {!isFetching && numberOfPages > 1 && (
                <Box
                    sx={{ marginTop: '3.2rem', justifyItems: 'center' }}
                    data-ai-explain="Pagination: page size and page navigation"
                >
                    <Pagination
                        count={numberOfPages}
                        size="large"
                        page={page}
                        onChange={handlePageChange}
                        parentIndex={9}
                        showPageSelection={true}
                        onPageSelection={handlePageSelection}
                        itemsPerPage={perPageItems}
                        showItemsPerPageSelection={true}
                        onItemsPerPageChange={handleItemsPerPageChange}
                        totalCount={data?.totalCount}
                        showCountStat={true}
                    />
                </Box>
            )}
        </Paper>
    )
}
export default SSOApplications
